import { useEffect, useMemo } from 'react'
import { useCoinBasis } from '@/hooks/coin'
import { useTranslation } from '@/services/i18n'
import {
  FieldRuleMap,
  useFeeValidate,
  FieldKeys,
  CustomizeFeeRateProps,
} from '@/hooks/fee/validator'
import {
  genInt,
  genIntOrDouble,
  genMaxOrEqualRule,
  genMinOrEqualRule,
  genMinRule,
  precision,
  RuleItem,
} from '@/utils/validator'

export const useSuiFeeRules = (dto: CustomizeFeeRateProps) => {
  const { coinKey, mini, isSpeed, max } = dto

  const { t } = useTranslation()
  const coinBasis = useCoinBasis(coinKey)

  const baseRule: RuleItem[] = [genIntOrDouble(), precision(coinBasis?.feeDecimal || 9)]
  const miniValueRule = useMemo<RuleItem | null>(
    () => (mini ? genMinRule(mini.feeRate!, t('fee.gasPriceLower_5')) : null),
    [mini]
  )

  // ----------- gasPrice rules --------------
  const gasPriceforceRules = useMemo<RuleItem[]>(() => {
    const tmp = [...baseRule, genMinRule(0, t('fee.greaterThan0_1'))]
    if (miniValueRule && !isSpeed) {
      tmp.push(miniValueRule)
    }
    return tmp
  }, [coinBasis, miniValueRule])
  const gasPriceWeakRules = useMemo<RuleItem[]>(() => {
    const rules: RuleItem[] = []

    if (max) {
      rules.push(genMaxOrEqualRule(max.feeRate!, t('fee.gasPriceHigher_1')))
    }
    return rules
  }, [mini?.feeRate, max?.feeRate, miniValueRule])

  // ----------- gasMaxFee rules --------------
  const gasMaxFeeForceRule = useMemo(() => {
    const gasLimitRule = [
      ...baseRule,
      genInt(),
      genMinOrEqualRule(2000, t('fee.lower_5', { min: 2000 })),
    ]
    return gasLimitRule
  }, [mini?.gasBudget])
  const gasMaxFeeWeakRule = useMemo(() => {
    const gasLimitRule: RuleItem[] = []
    if (mini) {
      gasLimitRule.push(genMinOrEqualRule(mini.gasBudget || 0, t('fee.gasLimitGreaterThan_1')))
    }
    return gasLimitRule
  }, [mini?.gasBudget])
  const fieldRuleMap: FieldRuleMap = {
    feeRate: {
      force: gasPriceforceRules,
      weak: gasPriceWeakRules,
    },
    gasBudget: {
      force: gasMaxFeeForceRule,
      weak: gasMaxFeeWeakRule,
    },
  }

  return fieldRuleMap
}

export const useValidateSuiFeeFields = (dto: CustomizeFeeRateProps) => {
  const { value, onChangeValue } = dto

  const fieldRuleMap = useSuiFeeRules(dto)

  const [errorMap, doValidate] = useFeeValidate()

  // ----------- validate service --------------
  const validteAndEmitChange = () => {
    const formIsError = doValidate(value, fieldRuleMap)
    onChangeValue && onChangeValue(formIsError, value)
  }

  const emitChange = <T extends FieldKeys>(fieldKey: T, text: string) => {
    const newValue = { ...value, [fieldKey]: text }
    onChangeValue && onChangeValue(true, newValue)
  }

  const handleChangeFeeRate = (text: string) => emitChange('feeRate', text)
  const handleChangeMaxFee = (text: string) => emitChange('maxFee', text)
  const handleChangeGasBudget = (text: string) => emitChange('gasBudget', text)
  useEffect(() => validteAndEmitChange(), [value])
  return {
    errorMap,
    handleChangeFeeRate,
    handleChangeMaxFee,
    handleChangeGasBudget,
  }
}
